import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, uploadFile } from "../../api/client";
import ConfirmModal from "../../components/ConfirmModal";
import { Field } from "./CartoDAEAdmin";

const TYPES = [
  { v: "compostelle", l: "Chemin de Compostelle" },
  { v: "rando", l: "Sentier de randonnée" },
  { v: "vtt", l: "VTT" },
  { v: "autre", l: "Autre" },
];
const labelType = (t) => (TYPES.find((x) => x.v === t) || { l: t }).l;

export default function CartoTracesAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get("/admin/cartographie/traces")).items || []); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async () => {
    try { await api.del(`/admin/cartographie/traces/${toDelete.id}`);
          setToDelete(null); load();
    } catch (e) { alert("Erreur : " + e.message); }
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
        <button onClick={() => setImporting(true)}>📂 Importer un GPX</button>
      </div>
      <p className="muted" style={{ fontSize: "0.85em", marginTop: "0.4rem" }}>
        Le format GPX est l'export standard des GPS et applis de rando (Komoot,
        Visorando, Géoportail). Le tracé est analysé automatiquement (distance,
        forme).
      </p>

      {loading && <p className="muted mt-2">Chargement…</p>}
      {!loading && items.length === 0 &&
        <p className="muted mt-2">Aucun sentier enregistré.</p>}
      {items.length > 0 && (
        <table className="admin-table mt-2"><thead><tr>
          <th>Nom</th><th>Type</th><th>Distance</th>
          <th>Difficulté</th><th>Actions</th>
        </tr></thead><tbody>
          {items.map((t) => (
            <tr key={t.id}>
              <td>
                <span style={{ display: "inline-block", width: 14, height: 14,
                               borderRadius: 2, background: t.couleur,
                               marginRight: 8, verticalAlign: "middle" }} />
                {t.nom}
              </td>
              <td className="muted">{labelType(t.type)}</td>
              <td className="muted">{t.distance_km ? `${t.distance_km} km` : "—"}</td>
              <td className="muted">{t.difficulte || "—"}</td>
              <td>
                <button className="small" onClick={() => setEditing(t)}>Modifier</button>
                <button className="small danger" style={{ marginLeft: 6 }}
                        onClick={() => setToDelete(t)}>Supprimer</button>
              </td>
            </tr>
          ))}
        </tbody></table>
      )}

      {importing && (
        <GPXImporter onClose={() => setImporting(false)}
                     onSaved={() => { setImporting(false); load(); }} />
      )}
      {editing && (
        <TraceEditor item={editing}
                     onClose={() => setEditing(null)}
                     onSaved={() => { setEditing(null); load(); }} />
      )}
      {toDelete && (
        <ConfirmModal title="Supprimer ce sentier ?"
                      message={`« ${toDelete.nom} » sera définitivement supprimé.`}
                      confirmLabel="Supprimer" variant="danger"
                      onConfirm={confirmDelete} onCancel={() => setToDelete(null)} />
      )}
    </div>
  );
}

function GPXImporter({ onClose, onSaved }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [nom, setNom] = useState("");
  const [type, setType] = useState("rando");
  const [difficulte, setDifficulte] = useState("");
  const [description, setDescription] = useState("");
  const [couleur, setCouleur] = useState("#d9a521");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const submit = async () => {
    setError(""); setSuccess(null);
    if (!file) { setError("Sélectionnez un fichier GPX."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("nom", nom);
      fd.append("type", type);
      fd.append("difficulte", difficulte);
      fd.append("description", description);
      fd.append("couleur", couleur);
      const r = await uploadFile("/admin/cartographie/traces/import-gpx", fd);
      setSuccess(r);
    } catch (e) { setError(e.message || "Échec de l'import"); }
    finally { setBusy(false); }
  };

  if (success) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 520 }}>
          <div className="modal-header">
            <h3>Import réussi</h3>
            <button className="modal-close" onClick={onSaved}>×</button>
          </div>
          <div className="modal-body">
            <div className="success">
              Sentier « {success.nom} » importé.<br/>
              {success.points} points · {success.distance_km} km
            </div>
          </div>
          <div className="modal-footer">
            <button onClick={onSaved}>OK</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Importer un fichier GPX</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="field"><label>Fichier GPX</label>
            <input ref={fileRef} type="file" accept=".gpx"
                   onChange={(e) => setFile(e.target.files[0] || null)} /></div>
          <Field label="Nom (laissé vide = nom du GPX)" value={nom} onChange={setNom} />
          <div className="field"><label>Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select></div>
          <Field label="Difficulté (facile, moyen, sportif…)" value={difficulte}
                 onChange={setDifficulte} />
          <div className="field"><label>Couleur</label>
            <input type="color" value={couleur}
                   onChange={(e) => setCouleur(e.target.value)}
                   style={{ width: 80, padding: 0, height: 36 }} /></div>
          <div className="field"><label>Description</label>
            <textarea rows={3} value={description}
                      onChange={(e) => setDescription(e.target.value)} /></div>
          {error && <div className="error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose}>Annuler</button>
          <button onClick={submit} disabled={busy || !file}>
            {busy ? "Import en cours…" : "Importer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TraceEditor({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    nom: item.nom || "",
    type: item.type || "rando",
    distance_km: item.distance_km || 0,
    difficulte: item.difficulte || "",
    description: item.description || "",
    couleur: item.couleur || "#d9a521",
    geojson: item.geojson,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    if (!form.nom.trim()) { setError("Le nom est obligatoire."); return; }
    setBusy(true);
    try {
      await api.put(`/admin/cartographie/traces/${item.id}`,
                    { ...form, nom: form.nom.trim() });
      onSaved();
    } catch (e) { setError(e.message || "Erreur"); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <h3>Modifier le sentier</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <p className="muted" style={{ fontSize: "0.85em" }}>
            Pour modifier le tracé lui-même, supprimez-le et ré-importez le GPX.
            Ici vous pouvez juste modifier les métadonnées.
          </p>
          <Field label="Nom" value={form.nom}
                 onChange={(v) => setForm({ ...form, nom: v })} />
          <div className="field"><label>Type</label>
            <select value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select></div>
          <Field label="Distance (km)" type="number" value={form.distance_km}
                 onChange={(v) => setForm({ ...form, distance_km: parseFloat(v) || 0 })} />
          <Field label="Difficulté" value={form.difficulte}
                 onChange={(v) => setForm({ ...form, difficulte: v })} />
          <div className="field"><label>Couleur</label>
            <input type="color" value={form.couleur}
                   onChange={(e) => setForm({ ...form, couleur: e.target.value })}
                   style={{ width: 80, padding: 0, height: 36 }} /></div>
          <div className="field"><label>Description</label>
            <textarea rows={3} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          {error && <div className="error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="secondary" onClick={onClose}>Annuler</button>
          <button onClick={submit} disabled={busy}>
            {busy ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
